import { BaseFeature } from '../../core/base-feature.js';

/**
 * EconomyFeature - Frontend feature for managing economy system
 * Handles: balances, transactions, visibility, tasks
 */
export class EconomyFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.currencies = [];
        this.balances = [];
        this.tasks = [];

        // Display state
        this.socketCount = 3;
        this.overlayPosition = 'top-right';

        // Event callbacks for React components
        this.balanceUpdateCallbacks = new Set();
        this.taskUpdateCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.gemClaimCallbacks = new Set();
        this.rewardExecutedCallbacks = new Set();

        // Auction callbacks
        this.auctionUpdateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'economy',
            name: 'Economy',
            version: 'v1.0',
            capabilities: [
                'manage_currencies',
                'manage_balances',
                'view_ledger',
                'manage_tasks'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Economy] Initializing...');
        console.log('[Economy] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;
        this._validateInbound(command, payload); // contract check (dev/test only)

        switch (command) {
            // Real-time updates
            case 'balance_update':
                return this.handleBalanceUpdate(payload);

            case 'task_update':
                return this.handleTaskUpdate(payload);

            case 'gem_claimed_from_desktop':
                return this.handleGemClaimFromDesktop(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            case 'reward_executed':
                return this.handleRewardExecuted(payload);

            // Responses to commands
            case 'get_currencies_response':
                return this.handleResponse('get_currencies', payload);

            case 'add_currency_response':
                return this.handleResponse('add_currency', payload);

            case 'get_balances_response':
                return this.handleResponse('get_balances', payload);

            case 'adjust_balance_response':
                return this.handleResponse('adjust_balance', payload);

            case 'set_visibility_response':
                return this.handleResponse('set_visibility', payload);

            case 'get_ledger_response':
                return this.handleResponse('get_ledger', payload);

            case 'create_task_response':
                return this.handleResponse('create_task', payload);

            case 'get_tasks_response':
                return this.handleResponse('get_tasks', payload);

            case 'get_task_response':
                return this.handleResponse('get_task', payload);

            case 'submit_task_response':
                return this.handleResponse('submit_task', payload);

            case 'approve_task_response':
                return this.handleResponse('approve_task', payload);

            case 'reject_task_response':
                return this.handleResponse('reject_task', payload);

            case 'delete_task_response':
                return this.handleResponse('delete_task', payload);

            case 'get_stats_response':
                return this.handleResponse('get_stats', payload);

            case 'show_display_response':
                return this.handleResponse('show_display', payload);
            case 'hide_display_response':
                return this.handleResponse('hide_display', payload);
            case 'set_socket_count_response':
                return this.handleResponse('set_socket_count', payload);
            case 'award_gem_response':
                return this.handleResponse('award_gem', payload);
            case 'claim_gem_response':
                return this.handleResponse('claim_gem', payload);
            case 'remove_gem_response':
                return this.handleResponse('remove_gem', payload);
            case 'get_display_state_response':
                return this.handleResponse('get_display_state', payload);
            case 'set_overlay_position_response':
                return this.handleResponse('set_overlay_position', payload);
            case 'set_desktop_clickthrough_response':
                return this.handleResponse('set_desktop_clickthrough', payload);
            case 'get_scoring_rules_response':
                return this.handleResponse('get_scoring_rules', payload);
            case 'set_scoring_rule_response':
                return this.handleResponse('set_scoring_rule', payload);
            case 'remove_scoring_rule_response':
                return this.handleResponse('remove_scoring_rule', payload);

            // Auction responses
            case 'create_listing_response':
                return this.handleResponse('create_listing', payload);
            case 'get_listings_response':
                return this.handleResponse('get_listings', payload);
            case 'get_listing_response':
                return this.handleResponse('get_listing', payload);
            case 'update_listing_response':
                return this.handleResponse('update_listing', payload);
            case 'delete_listing_response':
                return this.handleResponse('delete_listing', payload);
            case 'create_purchase_response':
                return this.handleResponse('create_purchase', payload);
            case 'approve_purchase_response':
                return this.handleResponse('approve_purchase', payload);
            case 'reject_purchase_response':
                return this.handleResponse('reject_purchase', payload);
            case 'get_purchases_response':
                return this.handleResponse('get_purchases', payload);

            // Auction real-time updates
            case 'auction_update':
                return this.handleAuctionUpdate(payload);

            default:
                console.warn(`[Economy] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleBalanceUpdate(payload) {
        console.log('[Economy] Balance update received');
        if (payload.balances) {
            this.balances = payload.balances;
        }
        this.notifyBalanceUpdateListeners(payload);
    }

    handleTaskUpdate(payload) {
        console.log('[Economy] Task update received:', payload.action);
        this.notifyTaskUpdateListeners(payload);
    }

    handleGemClaimFromDesktop(payload) {
        console.log('[Economy] Gem claimed from desktop:', payload.gemId);
        this.notifyGemClaimListeners(payload);
    }

    handleAuctionUpdate(payload) {
        console.log('[Economy] Auction update received');
        for (const callback of this.auctionUpdateCallbacks) {
            try { callback(payload); } catch (error) { console.error('[Economy] Error in auction update callback:', error); }
        }
    }

    handleRewardExecuted(payload) {
        console.log('[Economy] Reward executed:', payload.type, payload.label);
        this.notifyRewardExecutedListeners(payload);
    }

    handleInitialState(payload) {
        console.log('[Economy] Received initial state');
        if (payload.balances) {
            this.balances = payload.balances;
        }
        if (payload.pendingTasks) {
            this.tasks = payload.pendingTasks;
        }
        this.notifyUpdateListeners();
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Currency Methods
    // =====================================

    /**
     * Get all currencies
     * @returns {Promise<Object>} Currencies list
     */
    async getCurrencies() {
        return new Promise((resolve) => {
            this.sendMessage('get_currencies', {});
            this._pendingRequests.set('get_currencies', resolve);
        });
    }

    /**
     * Add a new currency
     * @param {string} currencyType - Currency type identifier
     * @param {string} displayName - Display name
     * @param {string} symbol - Currency symbol
     * @param {string} description - Description
     * @returns {Promise<Object>} Result
     */
    async addCurrency(currencyType, displayName, symbol, description) {
        return new Promise((resolve) => {
            this.sendMessage('add_currency', { currencyType, displayName, symbol, description });
            this._pendingRequests.set('add_currency', resolve);
        });
    }

    // =====================================
    // Balance Methods
    // =====================================

    /**
     * Get all balances
     * @param {boolean} includeHidden - Include hidden currencies
     * @returns {Promise<Object>} Balances list
     */
    async getBalances(includeHidden = false) {
        return new Promise((resolve) => {
            this.sendMessage('get_balances', { includeHidden });
            this._pendingRequests.set('get_balances', resolve);
        });
    }

    /**
     * Adjust a currency balance
     * @param {Object} options - Adjustment options
     * @param {string} options.currencyType - Currency type
     * @param {number} options.amount - Amount to add/subtract
     * @param {string} options.reason - Reason for adjustment
     * @param {string} options.sourceType - Source type
     * @param {number} options.sourceId - Source ID
     * @param {boolean} options.visibleToDesktop - Visible to desktop
     * @returns {Promise<Object>} Result
     */
    async adjustBalance(options) {
        return new Promise((resolve) => {
            this.sendMessage('adjust_balance', options);
            this._pendingRequests.set('adjust_balance', resolve);
        });
    }

    /**
     * Set visibility of a currency
     * @param {string} currencyType - Currency type
     * @param {boolean} visible - Visibility
     * @returns {Promise<Object>} Result
     */
    async setVisibility(currencyType, visible) {
        return new Promise((resolve) => {
            this.sendMessage('set_visibility', { currencyType, visible });
            this._pendingRequests.set('set_visibility', resolve);
        });
    }

    // =====================================
    // Ledger Methods
    // =====================================

    /**
     * Get transaction ledger
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Ledger entries
     */
    async getLedger(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_ledger', options);
            this._pendingRequests.set('get_ledger', resolve);
        });
    }

    // =====================================
    // Task Methods
    // =====================================

    /**
     * Create a new task
     * @param {Object} taskData - Task data
     * @returns {Promise<Object>} Result with task
     */
    async createTask(taskData) {
        return new Promise((resolve) => {
            this.sendMessage('create_task', taskData);
            this._pendingRequests.set('create_task', resolve);
        });
    }

    /**
     * Get tasks
     * @param {Object} options - Query options
     * @returns {Promise<Object>} Tasks list
     */
    async getTasks(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_tasks', options);
            this._pendingRequests.set('get_tasks', resolve);
        });
    }

    /**
     * Get a single task
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Task
     */
    async getTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('get_task', { taskId });
            this._pendingRequests.set('get_task', resolve);
        });
    }

    /**
     * Submit a task completion
     * @param {number} taskId - Task ID
     * @param {string} proofData - Optional proof data (base64 image)
     * @returns {Promise<Object>} Result
     */
    async submitTask(taskId, proofData = null) {
        return new Promise((resolve) => {
            this.sendMessage('submit_task', { taskId, proofData });
            this._pendingRequests.set('submit_task', resolve);
        });
    }

    /**
     * Approve a task
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Result
     */
    async approveTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('approve_task', { taskId });
            this._pendingRequests.set('approve_task', resolve);
        });
    }

    /**
     * Reject a task
     * @param {number} taskId - Task ID
     * @param {string} reason - Rejection reason
     * @returns {Promise<Object>} Result
     */
    async rejectTask(taskId, reason = null) {
        return new Promise((resolve) => {
            this.sendMessage('reject_task', { taskId, reason });
            this._pendingRequests.set('reject_task', resolve);
        });
    }

    /**
     * Delete a task
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Result
     */
    async deleteTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_task', { taskId });
            this._pendingRequests.set('delete_task', resolve);
        });
    }

    // =====================================
    // Stats
    // =====================================

    /**
     * Get economy statistics
     * @returns {Promise<Object>} Stats
     */
    async getStats() {
        return new Promise((resolve) => {
            this.sendMessage('get_stats', {});
            this._pendingRequests.set('get_stats', resolve);
        });
    }

    // =====================================
    // Display Overlay Methods
    // =====================================

    async showDisplay() {
        return new Promise((resolve) => {
            this.sendMessage('show_display', {});
            this._pendingRequests.set('show_display', resolve);
        });
    }

    async hideDisplay() {
        return new Promise((resolve) => {
            this.sendMessage('hide_display', {});
            this._pendingRequests.set('hide_display', resolve);
        });
    }

    async setSocketCount(count) {
        this.socketCount = count;
        return new Promise((resolve) => {
            this.sendMessage('set_socket_count', { count });
            this._pendingRequests.set('set_socket_count', resolve);
        });
    }

    async awardGem(gem) {
        return new Promise((resolve) => {
            this.sendMessage('award_gem', { gem });
            this._pendingRequests.set('award_gem', resolve);
        });
    }

    async removeGem(gemId) {
        return new Promise((resolve) => {
            this.sendMessage('remove_gem', { gemId });
            this._pendingRequests.set('remove_gem', resolve);
        });
    }

    async getDisplayState() {
        return new Promise((resolve) => {
            this.sendMessage('get_display_state', {});
            this._pendingRequests.set('get_display_state', resolve);
        });
    }

    async setOverlayPosition(position) {
        this.overlayPosition = position;
        return new Promise((resolve) => {
            this.sendMessage('set_overlay_position', { position });
            this._pendingRequests.set('set_overlay_position', resolve);
        });
    }

    async setDesktopClickthrough(enabled) {
        return new Promise((resolve) => {
            this.sendMessage('set_desktop_clickthrough', { enabled });
            this._pendingRequests.set('set_desktop_clickthrough', resolve);
        });
    }

    // =====================================
    // Scoring Rules Methods
    // =====================================

    async getScoringRules() {
        return new Promise((resolve) => {
            this.sendMessage('get_scoring_rules', {});
            this._pendingRequests.set('get_scoring_rules', resolve);
        });
    }

    async setScoringRule(rule) {
        return new Promise((resolve) => {
            this.sendMessage('set_scoring_rule', rule);
            this._pendingRequests.set('set_scoring_rule', resolve);
        });
    }

    async removeScoringRule(id) {
        return new Promise((resolve) => {
            this.sendMessage('remove_scoring_rule', { id });
            this._pendingRequests.set('remove_scoring_rule', resolve);
        });
    }

    // =====================================
    // Auction Methods
    // =====================================

    async createListing(data) {
        return new Promise((resolve) => {
            this.sendMessage('create_listing', data);
            this._pendingRequests.set('create_listing', resolve);
        });
    }

    async getListings(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_listings', options);
            this._pendingRequests.set('get_listings', resolve);
        });
    }

    async getListing(id) {
        return new Promise((resolve) => {
            this.sendMessage('get_listing', { id });
            this._pendingRequests.set('get_listing', resolve);
        });
    }

    async updateListing(id, changes) {
        return new Promise((resolve) => {
            this.sendMessage('update_listing', { id, changes });
            this._pendingRequests.set('update_listing', resolve);
        });
    }

    async deleteListing(id) {
        return new Promise((resolve) => {
            this.sendMessage('delete_listing', { id });
            this._pendingRequests.set('delete_listing', resolve);
        });
    }

    async createPurchase(listingId) {
        return new Promise((resolve) => {
            this.sendMessage('create_purchase', { listingId });
            this._pendingRequests.set('create_purchase', resolve);
        });
    }

    async approvePurchase(id) {
        return new Promise((resolve) => {
            this.sendMessage('approve_purchase', { id });
            this._pendingRequests.set('approve_purchase', resolve);
        });
    }

    async rejectPurchase(id, reason) {
        return new Promise((resolve) => {
            this.sendMessage('reject_purchase', { id, reason });
            this._pendingRequests.set('reject_purchase', resolve);
        });
    }

    async getPurchases(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_purchases', options);
            this._pendingRequests.set('get_purchases', resolve);
        });
    }

    onAuctionUpdate(callback) {
        this.auctionUpdateCallbacks.add(callback);
        return () => this.auctionUpdateCallbacks.delete(callback);
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to balance updates
     * @param {Function} callback - Called with balance data
     * @returns {Function} Unsubscribe function
     */
    onBalanceUpdate(callback) {
        this.balanceUpdateCallbacks.add(callback);
        return () => this.balanceUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to task updates
     * @param {Function} callback - Called with task data
     * @returns {Function} Unsubscribe function
     */
    onTaskUpdate(callback) {
        this.taskUpdateCallbacks.add(callback);
        return () => this.taskUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    /**
     * Subscribe to gem claims from desktop (when user redeems gem from overlay)
     * @param {Function} callback - Called with { gemId }
     * @returns {Function} Unsubscribe function
     */
    onGemClaimedFromDesktop(callback) {
        this.gemClaimCallbacks.add(callback);
        return () => this.gemClaimCallbacks.delete(callback);
    }

    notifyBalanceUpdateListeners(data) {
        for (const callback of this.balanceUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Economy] Error in balance update callback:', error);
            }
        }
    }

    notifyTaskUpdateListeners(data) {
        for (const callback of this.taskUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Economy] Error in task update callback:', error);
            }
        }
    }

    onRewardExecuted(callback) {
        this.rewardExecutedCallbacks.add(callback);
        return () => this.rewardExecutedCallbacks.delete(callback);
    }

    notifyRewardExecutedListeners(data) {
        for (const callback of this.rewardExecutedCallbacks) {
            try { callback(data); } catch (error) { console.error('[Economy] Error in reward executed callback:', error); }
        }
    }

    notifyGemClaimListeners(data) {
        for (const callback of this.gemClaimCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Economy] Error in gem claim callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            currencies: this.currencies,
            balances: this.balances,
            tasks: this.tasks
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Economy] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getCurrenciesList() {
        return this.currencies;
    }

    getBalancesList() {
        return this.balances;
    }

    getTasksList() {
        return this.tasks;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Economy] Cleaning up...');
        this.currencies = [];
        this.balances = [];
        this.tasks = [];
        this.balanceUpdateCallbacks.clear();
        this.taskUpdateCallbacks.clear();
        this.updateCallbacks.clear();
        this.gemClaimCallbacks.clear();
        this.rewardExecutedCallbacks.clear();
        this.auctionUpdateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
